import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Send, Mic, Paperclip, Square, Tag, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/LanguageContext";
import { transcribeAudio } from "@/api/sttApi";
import { getAuthToken } from "@/lib/auth";

const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL}/api/v1`;

export type DatabaseOption = { value: string; type: "Year" | "Geography" | "Indicator" };

const WAVE_BARS = 48;
const SILENCE_LEVEL = 0;
const WAVE_BAR_WIDTH_PX = 5;
const WAVE_BAR_GAP_PX = 2;

type SupportedSpeechLanguage = "en-US" | "am-ET";

function isValidSpeechTranscript(transcript: string): boolean {
  const normalized = transcript.trim();
  if (!normalized) {
    return false;
  }

  const meaningfulChars = Array.from(normalized).filter((char) =>
    /[\p{L}\p{N}]/u.test(char),
  );

  return meaningfulChars.length >= 2;
}

function getDefaultSpeechLanguage(): SupportedSpeechLanguage {
  if (
    typeof navigator !== "undefined" &&
    navigator.language.toLowerCase().startsWith("am")
  ) {
    return "am-ET";
  }

  return "en-US";
}

function getBackendSpeechLanguage(language: SupportedSpeechLanguage): string {
  return language === "am-ET" ? "am" : "en";
}

function mergeAudioChunks(chunks: Float32Array[]): Float32Array {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const merged = new Float32Array(totalLength);
  let offset = 0;

  chunks.forEach((chunk) => {
    merged.set(chunk, offset);
    offset += chunk.length;
  });

  return merged;
}

function writeWavString(view: DataView, offset: number, value: string): void {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

function encodeWavAudio(samples: Float32Array, sampleRate: number): Blob {
  const bytesPerSample = 2;
  const blockAlign = bytesPerSample;
  const buffer = new ArrayBuffer(44 + samples.length * bytesPerSample);
  const view = new DataView(buffer);

  writeWavString(view, 0, "RIFF");
  view.setUint32(4, 36 + samples.length * bytesPerSample, true);
  writeWavString(view, 8, "WAVE");
  writeWavString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeWavString(view, 36, "data");
  view.setUint32(40, samples.length * bytesPerSample, true);

  let offset = 44;
  for (let index = 0; index < samples.length; index += 1) {
    const clamped = Math.max(-1, Math.min(1, samples[index]));
    view.setInt16(
      offset,
      clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff,
      true,
    );
    offset += bytesPerSample;
  }

  return new Blob([buffer], { type: "audio/wav" });
}

interface ChatInputProps {
  onSend: (message: string, selectedFilters?: DatabaseOption[]) => void;
  loading: boolean;
  onStop?: () => void;
  placeholder?: string;
  className?: string;
  variant?: "default" | "floating";
  publicMode?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  loading,
  onStop,
  placeholder,
  className,
  variant = "default",
  publicMode = false,
}) => {
  const { t } = useLanguage();
  const [value, setValue] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [isTranscribingVoice, setIsTranscribingVoice] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [voicePrompt, setVoicePrompt] = useState("");
  const [speechLanguage, setSpeechLanguage] = useState<SupportedSpeechLanguage>(
    getDefaultSpeechLanguage(),
  );
  const [waveLevels, setWaveLevels] = useState<number[]>([]);
  const [waveBarCount, setWaveBarCount] = useState(WAVE_BARS);
  const [databaseOptions, setDatabaseOptions] = useState<DatabaseOption[]>([]);
  const [selectedDatabaseOptions, setSelectedDatabaseOptions] = useState<DatabaseOption[]>([]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputWaveRef = useRef<HTMLDivElement | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorNodeRef = useRef<ScriptProcessorNode | null>(null);
  const silentGainNodeRef = useRef<GainNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const recordedSamplesRef = useRef<Float32Array[]>([]);
  const recordingSampleRateRef = useRef(0);
  const clearAfterVoiceSendRef = useRef(false);
  const silenceDurationMsRef = useRef(0);
  const voiceSupportedRef = useRef(false);

  const ensureAudioStream = useCallback(async () => {
    if (audioStreamRef.current) {
      return audioStreamRef.current;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      return null;
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    audioStreamRef.current = stream;
    return stream;
  }, []);

  const stopAudioMeter = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    sourceNodeRef.current?.disconnect();
    analyserRef.current?.disconnect();
    processorNodeRef.current?.disconnect();
    silentGainNodeRef.current?.disconnect();
    sourceNodeRef.current = null;
    analyserRef.current = null;
    processorNodeRef.current = null;
    silentGainNodeRef.current = null;

    if (audioContextRef.current) {
      void audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }

    setWaveLevels([]);
    silenceDurationMsRef.current = 0;
  }, []);

  const startAudioMeter = useCallback(async () => {
    stopAudioMeter();

    try {
      const stream = await ensureAudioStream();
      if (!stream) {
        return;
      }

      const audioWindow = window as Window & {
        webkitAudioContext?: typeof AudioContext;
      };
      const AudioContextCtor =
        window.AudioContext || audioWindow.webkitAudioContext;

      if (!AudioContextCtor) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      const audioContext = new AudioContextCtor();
      const sourceNode = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      const processorNode = audioContext.createScriptProcessor(4096, 1, 1);
      const silentGainNode = audioContext.createGain();

      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.75;
      silentGainNode.gain.value = 0;

      sourceNode.connect(analyser);
      sourceNode.connect(processorNode);
      processorNode.connect(silentGainNode);
      silentGainNode.connect(audioContext.destination);

      recordedSamplesRef.current = [];
      recordingSampleRateRef.current = audioContext.sampleRate;
      processorNode.onaudioprocess = (event: AudioProcessingEvent) => {
        const channelData = event.inputBuffer.getChannelData(0);
        recordedSamplesRef.current.push(new Float32Array(channelData));
      };

      audioContextRef.current = audioContext;
      sourceNodeRef.current = sourceNode;
      analyserRef.current = analyser;
      processorNodeRef.current = processorNode;
      silentGainNodeRef.current = silentGainNode;
      setWaveLevels(Array.from({ length: waveBarCount }, () => 0));

      const timeData = new Uint8Array(analyser.fftSize);
      let smoothedLevel = SILENCE_LEVEL;
      let lastUpdateTs = 0;
      const updateEveryMs = 90;
      const silenceThreshold = 0.035;
      const silencePromptAfterMs = 3000;

      const tick = (ts: number) => {
        const activeAnalyser = analyserRef.current;
        if (!activeAnalyser) {
          return;
        }

        if (ts - lastUpdateTs < updateEveryMs) {
          animationFrameRef.current = requestAnimationFrame(tick);
          return;
        }
        lastUpdateTs = ts;

        activeAnalyser.getByteTimeDomainData(timeData);

        let sumSquares = 0;
        for (let i = 0; i < timeData.length; i += 1) {
          const centered = (timeData[i] - 128) / 128;
          sumSquares += centered * centered;
        }

        const rms = Math.sqrt(sumSquares / timeData.length);
        const boosted = Math.min(1, rms * 5.2);
        smoothedLevel = smoothedLevel * 0.82 + boosted * 0.18;
        if (smoothedLevel < 0.02) {
          smoothedLevel = 0;
        }

        if (smoothedLevel > silenceThreshold) {
          silenceDurationMsRef.current = 0;
          setVoicePrompt("Tap the mic again when you're done speaking.");
        } else {
          silenceDurationMsRef.current += updateEveryMs;
          if (silenceDurationMsRef.current >= silencePromptAfterMs) {
            setVoicePrompt(
              "I am not hearing anything clearly. Please speak up.",
            );
          }
        }

        setWaveLevels((prev) => {
          const targetCount = waveBarCount > 0 ? waveBarCount : WAVE_BARS;
          const seeded =
            prev.length === targetCount
              ? prev
              : Array.from({ length: targetCount }, () => 0);
          const next = [...seeded.slice(1), smoothedLevel];
          if (next.length > targetCount) {
            next.shift();
          }
          return next;
        });
        animationFrameRef.current = requestAnimationFrame(tick);
      };

      animationFrameRef.current = requestAnimationFrame(tick);
    } catch {
      setWaveLevels([]);
    }
  }, [ensureAudioStream, stopAudioMeter, waveBarCount]);

  const submitPrompt = useCallback(
    (prompt: string) => {
      const trimmed = prompt.trim();
      if (!trimmed || loading) {
        return false;
      }

      onSend(trimmed, selectedDatabaseOptions);
      setSelectedDatabaseOptions([]);
      return true;
    },
    [loading, onSend, selectedDatabaseOptions],
  );

  const toggleSpeechLanguage = useCallback(() => {
    if (loading || isListening || isTranscribingVoice) {
      return;
    }

    setVoiceError("");
    setVoicePrompt("");
    setSpeechLanguage((current) => (current === "en-US" ? "am-ET" : "en-US"));
  }, [isListening, isTranscribingVoice, loading]);

  const transcribeRecording = useCallback(
    async (blob: Blob) => {
      setIsTranscribingVoice(true);
      setVoiceError("");
      setVoicePrompt("Transcribing audio...");

      try {
        const transcriptResponse = await transcribeAudio({
          blob,
          filename: "voice-input.wav",
          language: getBackendSpeechLanguage(speechLanguage),
        });

        const transcript = (transcriptResponse.text || "").trim();
        if (!transcript || !isValidSpeechTranscript(transcript)) {
          setValue("");
          setVoiceError(
            transcript
              ? "I could not understand that clearly. Please speak up and try again."
              : "I am not hearing anything. Please speak up and try again.",
          );
          return;
        }

        setValue(transcript);
        setVoicePrompt("");
        if (submitPrompt(transcript)) {
          clearAfterVoiceSendRef.current = true;
        }
      } catch (error) {
        setValue("");
        setVoiceError(
          error instanceof Error
            ? error.message
            : "Voice transcription failed. Please try again.",
        );
      } finally {
        setIsTranscribingVoice(false);
        setVoicePrompt("");
      }
    },
    [speechLanguage, submitPrompt],
  );

  const stopListening = useCallback(() => {
    const recordedChunks = recordedSamplesRef.current;
    recordedSamplesRef.current = [];
    const sampleRate = recordingSampleRateRef.current;
    recordingSampleRateRef.current = 0;

    if (!recordedChunks.length || sampleRate <= 0) {
      stopAudioMeter();
      setIsListening(false);
      setVoicePrompt("");
      setVoiceError("No speech detected. Please try again.");
      return;
    }

    const mergedSamples = mergeAudioChunks(recordedChunks);
    const wavBlob = encodeWavAudio(mergedSamples, sampleRate);
    setIsListening(false);
    stopAudioMeter();
    void transcribeRecording(wavBlob);
  }, [stopAudioMeter, transcribeRecording]);

  const handleVoiceToggle = useCallback(async () => {
    setVoiceError("");
    if (loading || isTranscribingVoice) {
      return;
    }

    if (isListening) {
      setVoicePrompt("Transcribing audio...");
      stopListening();
      return;
    }

    if (
      typeof window === "undefined" ||
      !(window.AudioContext ||
        (window as Window & { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext) ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      setVoiceError("Voice input is not supported in this browser.");
      return;
    }

    try {
      const stream = await ensureAudioStream();
      if (!stream) {
        setVoiceError("Unable to access the microphone.");
        return;
      }

      setValue("");
      setVoicePrompt("Tap the mic again when you're done speaking.");
      setIsListening(true);
      void startAudioMeter();
    } catch {
      setVoiceError("Unable to start voice input. Please try again.");
      stopAudioMeter();
    }
  }, [
    ensureAudioStream,
    isListening,
    isTranscribingVoice,
    loading,
    startAudioMeter,
    stopAudioMeter,
    stopListening,
    transcribeRecording,
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitPrompt(value)) {
      setValue("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [value]);

  useEffect(() => {
    const node = inputWaveRef.current;
    if (!node) {
      return;
    }

    const updateCount = () => {
      const width = node.clientWidth;
      const count = Math.max(
        24,
        Math.floor(width / (WAVE_BAR_WIDTH_PX + WAVE_BAR_GAP_PX)),
      );
      setWaveBarCount(count);
    };

    updateCount();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateCount);
      return () => window.removeEventListener("resize", updateCount);
    }

    const observer = new ResizeObserver(() => updateCount());
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isListening) {
      return;
    }

    setWaveLevels((prev) => {
      if (prev.length === waveBarCount) {
        return prev;
      }
      if (prev.length > waveBarCount) {
        return prev.slice(prev.length - waveBarCount);
      }
      return [
        ...Array.from({ length: waveBarCount - prev.length }, () => 0),
        ...prev,
      ];
    });
  }, [isListening, waveBarCount]);

  useEffect(() => {
    if (!voiceError) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setVoiceError("");
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [voiceError]);

  useEffect(() => {
    const hasVoiceInput =
      typeof window !== "undefined" &&
      Boolean(
        window.AudioContext ||
          (window as Window & { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext,
      ) &&
      typeof navigator !== "undefined" &&
      Boolean(navigator.mediaDevices?.getUserMedia);

    voiceSupportedRef.current = hasVoiceInput;
    setVoiceSupported(hasVoiceInput);

    return () => {
      stopAudioMeter();
    };
  }, [stopAudioMeter]);

  useEffect(() => {
    const controller = new AbortController();
    const loadDatabaseOptions = async () => {
      try {
        const token = getAuthToken();
        const response = await fetch(
          `${API_BASE_URL}/${publicMode ? "public/" : ""}anomalies/options`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            signal: controller.signal,
          },
        );
        if (!response.ok) return;
        const payload = await response.json() as {
          years?: string[];
          indicators?: string[];
          org_units?: Array<{ name?: string }>;
        };
        const unique = new Map<string, DatabaseOption>();
        (payload.years || []).forEach((year) => unique.set(`Year:${year}`, { value: String(year), type: "Year" }));
        (payload.org_units || []).forEach((unit) => {
          if (unit.name) unique.set(`Geography:${unit.name}`, { value: unit.name, type: "Geography" });
        });
        (payload.indicators || []).forEach((indicator) => unique.set(`Indicator:${indicator}`, { value: indicator, type: "Indicator" }));
        setDatabaseOptions([...unique.values()]);
      } catch {
        // Suggestions are optional; the chat composer still works offline.
      }
    };
    void loadDatabaseOptions();
    return () => controller.abort();
  }, [publicMode]);

  useEffect(() => {
    if (
      loading ||
      isListening ||
      isTranscribingVoice ||
      !clearAfterVoiceSendRef.current
    ) {
      return;
    }

    clearAfterVoiceSendRef.current = false;
    setValue("");
  }, [loading, isListening, isTranscribingVoice]);

  const canUseVoice = voiceSupportedRef.current;

  const activePlaceholder = placeholder || t("input.placeholder");
  const quickSuggestions = [
    t("input.suggestion.1"),
    t("input.suggestion.2"),
    t("input.suggestion.3"),
    t("input.suggestion.4"),
    t("input.suggestion.5"),
    t("input.suggestion.6"),
  ];

  const isFloating = variant === "floating";
  const databaseSuggestions = useMemo(() => {
    const trailingText = value.match(/([\p{L}\p{N}][\p{L}\p{N}\s-]{1,})$/u)?.[1] || "";
    const queries = trailingText
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .map((_, index, words) => words.slice(index).join(" "))
      .filter((query) => query.length >= 2);
    if (queries.length === 0) return [];
    return databaseOptions
      .filter((option) => queries.some((query) => option.value.toLowerCase().includes(query)))
      .filter((option) => !selectedDatabaseOptions.some((selected) => selected.type === option.type && selected.value === option.value))
      .slice(0, 6);
  }, [databaseOptions, selectedDatabaseOptions, value]);

  return (
    <div className={cn(isFloating ? "p-0" : "p-2.5", className)}>
      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
        <div
          className={cn(
            "relative bg-white backdrop-blur-sm border border-secondary-200/50",
            isFloating
              ? "rounded-2xl shadow-xl shadow-cyan-100/40 ring-1 ring-cyan-100/60"
              : "rounded-lg shadow-sm",
          )}
        >
          <div className="flex items-end gap-2 p-2.5">
            <button
              type="button"
              className="flex-shrink-0 w-8 h-8 rounded-md bg-secondary-100 hover:bg-secondary-200 flex items-center justify-center transition-colors duration-200 disabled:opacity-50"
              disabled={loading || isTranscribingVoice}
            >
              <Paperclip className="w-3.5 h-3.5 text-secondary-600" />
            </button>

            <div ref={inputWaveRef} className="flex-1 relative">
              {selectedDatabaseOptions.length > 0 && (
                <div className="mb-1 flex flex-wrap gap-1">
                  {selectedDatabaseOptions.map((option) => (
                    <button
                      key={`${option.type}-${option.value}`}
                      type="button"
                      onClick={() => setSelectedDatabaseOptions((selected) => selected.filter((item) => item.type !== option.type || item.value !== option.value))}
                      className="inline-flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-700 hover:bg-primary-100"
                      title={`Remove ${option.value}`}
                    >
                      <Tag className="h-2.5 w-2.5" />
                      {option.value}
                      <X className="h-2.5 w-2.5" />
                    </button>
                  ))}
                </div>
              )}
              <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isListening || isTranscribingVoice ? "" : activePlaceholder}
                disabled={loading || isTranscribingVoice}
                rows={1}
                className={cn(
                  "w-full resize-none bg-transparent border-0 px-0 py-1 text-[13px] text-secondary-900 placeholder:text-secondary-500 focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed",
                  isListening && "pb-5",
                )}
                style={{ minHeight: "22px", maxHeight: "104px" }}
              />

              {isListening && (
                <div className="pointer-events-none absolute inset-x-0 bottom-1">
                  <div className="relative h-8 w-full overflow-hidden">
                    <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-black/10"></div>
                    <div className="relative h-full w-full flex items-center justify-start gap-[2px] opacity-60">
                      {waveLevels.map((level, index) => (
                        <span
                          key={`wave-${index}`}
                          className="rounded-sm bg-black"
                          style={{
                            width: `${WAVE_BAR_WIDTH_PX}px`,
                            height: `${Math.round(level * 30)}px`,
                            transition: "height 130ms ease-out",
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {value.length > 0 && !isListening && !isTranscribingVoice && (
                <div className="absolute bottom-0 right-0 text-xs text-secondary-400">
                  {value.length}
                </div>
              )}
              {databaseSuggestions.length > 0 && !isListening && !isTranscribingVoice && (
                <div className="absolute bottom-full left-0 z-30 mb-2 w-full overflow-hidden rounded-lg border border-primary-100 bg-white shadow-lg">
                  <p className="border-b border-slate-100 px-2 py-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                    Available in database
                  </p>
                  {databaseSuggestions.map((option) => (
                    <button
                      key={`${option.type}-${option.value}`}
                      type="button"
                      onClick={() => {
                        setSelectedDatabaseOptions((selected) => [...selected, option]);
                        setValue((current) => current.replace(/([\p{L}\p{N}][\p{L}\p{N}\s-]{1,})$/u, "").trimEnd() + " ");
                        textareaRef.current?.focus();
                      }}
                      className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-[11px] text-slate-700 hover:bg-primary-50"
                    >
                      <span className="rounded bg-primary-50 px-1.5 py-0.5 text-[9px] font-semibold text-primary-600">{option.type}</span>
                      <span className="truncate">{option.value}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              type="button"
              className={cn(
                "flex-shrink-0 min-w-9 h-8 rounded-md border text-[11px] font-semibold transition-colors duration-200 disabled:opacity-50",
                speechLanguage === "am-ET"
                  ? "border-primary-300 bg-primary-50 text-primary-700"
                  : "border-secondary-200 bg-secondary-50 text-secondary-700",
              )}
              disabled={
                loading || isListening || isTranscribingVoice || !voiceSupported
              }
              onClick={toggleSpeechLanguage}
              title={
                speechLanguage === "am-ET"
                  ? "Transcription language hint: Amharic"
                  : "Transcription language hint: English"
              }
            >
              {speechLanguage === "am-ET" ? "AM" : "EN"}
            </button>

            <button
              type="button"
              className={cn(
                "flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center transition-colors duration-200 disabled:opacity-50",
                isListening
                  ? "bg-error-100 hover:bg-error-200"
                  : "bg-secondary-100 hover:bg-secondary-200",
              )}
              disabled={loading || isTranscribingVoice || !canUseVoice}
              onClick={() => {
                void handleVoiceToggle();
              }}
              title={isListening ? "Stop voice input" : "Start voice input"}
            >
              <Mic
                className={cn(
                  "w-3.5 h-3.5",
                  isListening ? "text-error-600" : "text-secondary-600",
                )}
              />
            </button>

            {loading && onStop ? (
              <Button
                type="button"
                onClick={onStop}
                className={cn(
                  "flex-shrink-0 h-8 px-2.5 rounded-md transition-all duration-200",
                  "bg-error-600 hover:bg-error-700 text-white shadow-lg hover:shadow-xl",
                )}
              >
                <Square className="w-3.5 h-3.5" />
                <span className="sr-only">{t("btn.stop")}</span>
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={!value.trim() || loading || isTranscribingVoice}
                loading={loading}
                className={cn(
                  "flex-shrink-0 h-8 px-2.5 rounded-md transition-all duration-200",
                  value.trim() && !loading && !isTranscribingVoice
                    ? "bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 shadow-lg hover:shadow-xl"
                    : "bg-secondary-200 text-secondary-500",
                )}
              >
                <Send className="w-3.5 h-3.5" />
                <span className="sr-only">Send message</span>
              </Button>
            )}
          </div>

          {(isListening || isTranscribingVoice) && voicePrompt && (
            <div className="px-4 pb-2 text-xs">
              <p className="text-secondary-500">{voicePrompt}</p>
            </div>
          )}

          {!isListening && !isTranscribingVoice && voiceError && (
            <div className="px-4 pb-3 text-xs">
              <p className="text-error-600">{voiceError}</p>
            </div>
          )}

          {value.length === 0 && !loading && !isListening && !isTranscribingVoice && !isFloating && (
            <div className="px-2.5 pb-2.5">
              <div className="flex flex-wrap gap-1.5">
                {quickSuggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => setValue(suggestion)}
                    className="px-2 py-0.5 text-[11px] font-medium text-secondary-600 bg-secondary-50 hover:bg-secondary-100 rounded-md transition-colors duration-200"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </form>
      <p className="mx-auto mt-2 max-w-4xl px-2 text-center text-[10px] leading-4 text-secondary-500">
        {t("input.disclaimer")}
      </p>
    </div>
  );
};
